import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  activateAdminProduct,
  archiveAdminProduct,
  bulkUpdateAdminProducts,
  createAdminProduct,
  fetchAdminProduct,
  fetchAdminProducts,
  updateAdminProduct,
  type CreateProductInput,
  type UpdateProductInput,
} from '../api/catalog-api.js';
import type { BulkProductAction, ProductListParams } from '../types/catalog.js';

const PRODUCTS_KEY = ['admin', 'catalog', 'products'] as const;

function productsListKey(params: ProductListParams) {
  return [...PRODUCTS_KEY, 'list', params] as const;
}

function productDetailKey(id: string) {
  return [...PRODUCTS_KEY, 'detail', id] as const;
}

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof AdminApiError && [401, 403, 404, 422].includes(error.status);
}

export function useAdminProducts(params: ProductListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: productsListKey(params),
    queryFn: async ({ signal }) => fetchAdminProducts(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminProduct(id: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: productDetailKey(id ?? ''),
    queryFn: async ({ signal }) => fetchAdminProduct(id!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(id),
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

/** Every product mutation invalidates the list + this product's detail query -- never an optimistic update, since a product's derived list fields (stock summary, variant count) are server-computed and would be wrong to guess client-side (Part 16 "avoid optimistic updates ... unless rollback is correct"). */
export function useAdminProductMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
  }

  const create = useMutation({
    mutationFn: async (input: CreateProductInput) =>
      createAdminProduct(input, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateProductInput }) =>
      updateAdminProduct(id, input, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  const archive = useMutation({
    mutationFn: async (id: string) => archiveAdminProduct(id, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  const activate = useMutation({
    mutationFn: async (id: string) => activateAdminProduct(id, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  const bulkUpdate = useMutation({
    mutationFn: async (input: BulkProductAction) =>
      bulkUpdateAdminProducts(input, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  return { create, update, archive, activate, bulkUpdate };
}
